import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Tag, Button, Select, Tooltip, Popconfirm } from "antd";
import { syncDIDNumbers, fetchAdminDIDNumbers } from "../../../core/data/redux/slices/didlogicAdminSlice";
import { showToast } from "../../../core/data/redux/slices/ToastSlice";
import api from "../../../core/axios/axiosInstance";

const { Option } = Select;

const AdminDIDNumbers = () => {
  const dispatch = useDispatch();
  const { allNumbers, allNumbersTotal, numbersLoading, syncing } = useSelector((s) => s.didlogicAdmin);

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");

  // Company admin list for the assignment dropdown
  const [companyAdmins, setCompanyAdmins] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);

  // Per-row assignment state: rowId → { selectedAdminId, saving }
  const [assignState, setAssignState] = useState({});

  const loadNumbers = useCallback((p = page, s = statusFilter) => {
    const params = { page: p, limit: 50 };
    if (s) params.status = s;
    dispatch(fetchAdminDIDNumbers(params));
  }, [dispatch, page, statusFilter]);

  useEffect(() => {
    loadNumbers(1);
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setAdminsLoading(true);
    api.get("/superAdmin/didlogic/company-admins")
      .then((res) => setCompanyAdmins(res.data?.data || []))
      .catch(() => {})
      .finally(() => setAdminsLoading(false));
  }, []);

  const handleSync = async () => {
    await dispatch(syncDIDNumbers());
    loadNumbers(1);
  };

  const handleStatusFilter = (v) => {
    setStatusFilter(v || "");
    setPage(1);
    loadNumbers(1, v || "");
  };

  const setRowAdmin = (rowId, adminId) => {
    setAssignState((prev) => ({ ...prev, [rowId]: { ...prev[rowId], selectedAdminId: adminId } }));
  };

  const handleAssign = async (record) => {
    const { selectedAdminId } = assignState[record._id] || {};
    setAssignState((prev) => ({ ...prev, [record._id]: { ...prev[record._id], saving: true } }));
    try {
      await api.put(`/superAdmin/didlogic/numbers/${record._id}/assign-company`, {
        companyAdminId: selectedAdminId || null,
      });
      const admin = companyAdmins.find((a) => a._id === selectedAdminId);
      const msg = selectedAdminId
        ? `+${record.number} assigned to ${admin?.firstname} ${admin?.lastname}`
        : `+${record.number} returned to inventory`;
      dispatch(showToast({ message: msg, variant: "success" }));
      loadNumbers();
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Assignment failed", variant: "danger" }));
    } finally {
      setAssignState((prev) => ({ ...prev, [record._id]: { ...prev[record._id], saving: false } }));
    }
  };

  const columns = [
    {
      title: "Phone Number",
      dataIndex: "number",
      key: "number",
      render: (v) => <span className="fw-semibold font-monospace">+{v}</span>,
    },
    {
      title: "Country",
      dataIndex: "countryName",
      key: "countryName",
      render: (v) => <Tag color="blue">{v || "—"}</Tag>,
    },
    {
      title: "Area",
      dataIndex: "area",
      key: "area",
      render: (v) => v || "—",
    },
    {
      title: "Provider Cost",
      dataIndex: "didlogicMonthlyFee",
      key: "didlogicMonthlyFee",
      render: (v) => `$${Number(v || 0).toFixed(2)}/mo`,
      align: "right",
    },
    {
      title: "Customer Price",
      dataIndex: "ourMonthlyPrice",
      key: "ourMonthlyPrice",
      render: (v, r) => (
        <div>
          <span className="fw-semibold text-success">${Number(v || 0).toFixed(2)}/mo</span>
          <small className="text-muted d-block">{r.marginPercent}% margin</small>
        </div>
      ),
      align: "right",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v) => <Tag color={v === "assigned" ? "green" : "default"}>{v}</Tag>,
    },
    {
      title: "Assigned To",
      dataIndex: "companyAdminId",
      key: "companyAdminId",
      render: (v) =>
        v ? (
          <span>
            {v.firstname} {v.lastname}
            <small className="text-muted d-block">{v.email}</small>
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      title: "Assign to Company Admin",
      key: "assign",
      width: 320,
      render: (_, record) => {
        const row = assignState[record._id] || {};
        const currentAdminId = record.companyAdminId?._id || null;
        const selectedAdminId = row.selectedAdminId !== undefined ? row.selectedAdminId : currentAdminId;
        const changed = selectedAdminId !== currentAdminId;

        return (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Select
              loading={adminsLoading}
              value={selectedAdminId || undefined}
              placeholder="Select company admin…"
              allowClear
              style={{ minWidth: 190 }}
              onChange={(v) => setRowAdmin(record._id, v || null)}
              onClear={() => setRowAdmin(record._id, null)}
              showSearch
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {companyAdmins.map((a) => (
                <Option key={a._id} value={a._id}>
                  {a.firstname} {a.lastname}
                </Option>
              ))}
            </Select>

            {changed && (
              selectedAdminId ? (
                <Button
                  type="primary"
                  size="small"
                  loading={row.saving}
                  onClick={() => handleAssign(record)}
                >
                  Assign
                </Button>
              ) : (
                <Popconfirm
                  title="Return this number to inventory?"
                  onConfirm={() => handleAssign(record)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button danger size="small" loading={row.saving}>
                    Unassign
                  </Button>
                </Popconfirm>
              )
            )}

            {!changed && currentAdminId && (
              <Tooltip title="Unassign — clear the dropdown and click Unassign">
                <Tag color="green" style={{ margin: 0 }}>Assigned</Tag>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Assigned At",
      dataIndex: "assignedAt",
      key: "assignedAt",
      render: (v) => (v ? new Date(v).toLocaleDateString() : "—"),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row align-items-center mb-4">
          <div className="col">
            <h4 className="fw-bold mb-0">DID Numbers</h4>
            <p className="text-muted mb-0">
              Assign numbers to company admins — one number can only belong to one company at a time,
              but a company can have as many numbers as you assign.
            </p>
          </div>
          <div className="col-auto d-flex gap-2">
            <Select
              placeholder="All statuses"
              allowClear
              style={{ width: 150 }}
              onChange={handleStatusFilter}
            >
              <Option value="available">Available</Option>
              <Option value="assigned">Assigned</Option>
            </Select>
            <Button type="primary" onClick={handleSync} loading={syncing}>
              <i className="ti ti-refresh me-1" />
              Sync from DIDProvider
            </Button>
          </div>
        </div>

        <div className="card">
          <div className="card-body p-0">
            <Table
              dataSource={allNumbers}
              columns={columns}
              rowKey="_id"
              loading={numbersLoading}
              scroll={{ x: 1100 }}
              pagination={{
                total: allNumbersTotal,
                pageSize: 50,
                current: page,
                onChange: (p) => { setPage(p); loadNumbers(p); },
                showTotal: (t) => `${t} numbers`,
              }}
              locale={{ emptyText: 'No numbers found. Click "Sync from DIDProvider" to import your numbers.' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDIDNumbers;
